import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Instagram, Newspaper, ExternalLink, Globe } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { PROJECTS, MARKET_SECTIONS, youtubeId } from '../data/projects';
import { CAPABILITY_FILTERS } from '../data/skills';
import { lenisRef } from '../lib/atlas';

const EASE = [0.22, 1, 0.36, 1];

/* ── Timeline spine: gradient line that illuminates with scroll ── */
function Spine({ trackRef }) {
  const fillRef = useRef();
  const dotRef = useRef();

  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = trackRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const total = Math.max(1, r.height - window.innerHeight * 0.5);
      const p = Math.min(1, Math.max(0, (-r.top - window.innerHeight * 0.2) / total));
      if (fillRef.current) fillRef.current.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
      if (dotRef.current) dotRef.current.style.top = `${p * 100}%`;
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 bottom-0 left-[7px] md:left-1/2 md:-translate-x-1/2 w-[2px]"
      data-testid="timeline-spine"
    >
      <div className="absolute inset-0 accent-gradient-v opacity-[0.13]" />
      <div ref={fillRef} className="absolute inset-0 accent-gradient-v" style={{ clipPath: 'inset(0 0 100% 0)' }} />
      <div ref={dotRef} className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0">
        <div className="w-3 h-3 rounded-full accent-gradient ring-4 ring-void" />
      </div>
    </div>
  );
}

/* ── Media: lazy YouTube + honest link cards ── */
function YouTubeCard({ url, label }) {
  const [play, setPlay] = useState(false);
  const id = youtubeId(url);
  if (!id) return null;
  return (
    <div data-testid="yt-card">
      <div className="relative aspect-video border border-white/10 overflow-hidden group bg-deep">
        {play ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={label || 'Campaign video'}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            onClick={() => setPlay(true)}
            className="absolute inset-0 w-full"
            aria-label={label ? `Play: ${label}` : 'Play video'}
            data-testid="yt-play-button"
          >
            <img
              src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-11 h-11 rounded-full bg-void/85 border border-white/25 group-hover:border-tangerine group-hover:scale-110 transition-all duration-300 flex items-center justify-center">
                <Play size={15} className="text-white ml-0.5" />
              </span>
            </span>
            {label && (
              <span className="absolute bottom-0 left-0 right-0 bg-void/85 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-300 text-left">
                {label}
              </span>
            )}
          </button>
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-1.5 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600 hover:text-slate-300 transition-colors"
      >
        Watch on YouTube <ExternalLink size={9} />
      </a>
    </div>
  );
}

function LinkCard({ url, type, label }) {
  const isIG = type === 'instagram';
  const host = url.includes('instagram.com') ? 'Instagram' : new URL(url).hostname.replace('www.', '');
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`${type}-link-card`}
      className="group relative flex flex-col justify-between border border-white/10 bg-deep/60 p-4 min-h-[118px] hover:border-magenta/50 transition-colors duration-300"
    >
      <div className="flex items-start justify-between">
        {isIG ? (
          <Instagram size={16} className="text-slate-500 group-hover:text-magenta transition-colors" />
        ) : (
          <Newspaper size={16} className="text-slate-500 group-hover:text-magenta transition-colors" />
        )}
        <ExternalLink size={11} className="text-slate-700 group-hover:text-slate-400 transition-colors" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 leading-relaxed">
          {label || host}
        </p>
        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600 group-hover:text-slate-400 transition-colors">
          View {isIG ? 'on Instagram' : `on ${host}`}
        </p>
      </div>
    </a>
  );
}

function MediaGroups({ groups }) {
  return (
    <div className="mt-6 space-y-5" data-testid="project-media">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500 mb-3">
            {g.label}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-start">
            {g.items.map((m, i) =>
              m.type === 'youtube' ? (
                <YouTubeCard key={i} url={m.url} label={m.label} />
              ) : (
                <LinkCard key={i} url={m.url} type={m.type} label={m.label} />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Uniform project entry ── */
function Entry({ p, num, side }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.8, ease: EASE }}
      className={`relative pl-12 md:pl-0 pb-14 ${
        side === 'left'
          ? 'md:w-[calc(50%-3rem)] md:mr-auto'
          : 'md:w-[calc(50%-3rem)] md:ml-auto'
      }`}
      data-testid={`timeline-entry-${p.id}`}
      id={`p-${p.id}`}
    >
      {/* node marker on the spine */}
      <motion.span
        initial={{ scale: 0.5, opacity: 0.4 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.5, ease: EASE }}
        className={`absolute top-8 w-3 h-3 rounded-full bg-void border-2 border-tangerine ${
          side === 'left'
            ? 'left-[-41px] md:left-auto md:right-[-53px]'
            : 'left-[-41px] md:left-[-53px]'
        }`}
        aria-hidden="true"
      />

      <div className="border border-white/[0.07] bg-deep/30 p-6 sm:p-8 hover:border-white/[0.15] transition-colors duration-500 h-full">
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <span className="font-mono text-[11px] tracking-[0.25em] text-tangerine">
            {String(num).padStart(2, '0')}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {p.date}
          </span>
        </div>

        <h3 className="mt-4 font-syne font-extrabold text-xl sm:text-2xl text-white tracking-tight">
          {p.brand}
        </h3>
        {p.title && (
          <p className="mt-1.5 font-serif italic text-lg text-violettext leading-snug">{p.title}</p>
        )}

        <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 border border-white/10">
          <Globe size={11} className="text-ultraviolet" />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-violettext">
            {p.location}
          </span>
        </div>

        {p.agency && (
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
            Agency — {p.agency}
          </p>
        )}
        <p className="mt-3 text-sm text-slate-300">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Role — </span>
          {p.role}
        </p>

        {p.description && (
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">{p.description}</p>
        )}

        {p.outcomes && p.outcomes.length > 0 && (
          <ul className="mt-5 space-y-2">
            {p.outcomes.map((o) => (
              <li key={o} className="flex gap-3 text-sm text-slate-200 leading-relaxed">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-tangerine shrink-0" />
                {o}
              </li>
            ))}
          </ul>
        )}

        {p.skills && p.skills.length > 0 && (
          <p className="mt-5 text-sm text-violettext leading-relaxed">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Skills — </span>
            {p.skills.join(' · ')}
          </p>
        )}

        {p.notes &&
          p.notes.map((n) => (
            <p key={n} className="mt-4 text-[13px] italic text-slate-500 leading-relaxed">
              {n}
            </p>
          ))}

        {p.mediaGroups && <MediaGroups groups={p.mediaGroups} />}
      </div>
    </motion.article>
  );
}

export default function Work() {
  const [marketFilter, setMarketFilter] = useState('all');
  const [capId, setCapId] = useState(null);
  const trackRef = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (!el) return;
      if (lenisRef.current) lenisRef.current.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  const cap = CAPABILITY_FILTERS.find((c) => c.id === capId);
  const filtered = PROJECTS.filter(
    (p) => (marketFilter === 'all' || p.cluster === marketFilter) && (!cap || p.skills.some((s) => cap.match.test(s)))
  );

  return (
    <div className="grain relative min-h-screen bg-void">
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 70% 20%, rgba(113,59,255,0.14), transparent 60%), radial-gradient(ellipse 50% 40% at 20% 80%, rgba(255,113,56,0.07), transparent 60%)',
        }}
        aria-hidden="true"
      />
      <Nav />

      <main className="relative z-10" data-testid="work-page-container">
        {/* Header */}
        <section className="px-6 lg:px-16 pt-36 pb-12">
          <div className="max-w-[1400px] mx-auto">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-tangerine">
                The Portfolio — Markets, Brands, Roles
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-syne font-extrabold text-white tracking-tight leading-[0.95] text-[clamp(3rem,8vw,7rem)]">
                ALL WORK
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-8 h-px w-32 bg-gradient-to-r from-ultraviolet to-tangerine" />
            </Reveal>
            <Reveal delay={0.24}>
              <p className="mt-8 max-w-xl text-base text-slate-400 leading-relaxed">
                A chronological record of campaigns and engagements across four markets — same
                structure for every project: dates, brand, role, skills and the work itself.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/"
                data-testid="work-return-atlas"
                className="mt-8 inline-flex items-center gap-2.5 px-5 py-3 border border-white/15 hover:border-tangerine/70 transition-colors duration-300"
              >
                <ArrowLeft size={13} className="text-tangerine" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white">
                  Return to the Atlas
                </span>
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Filters */}
        <section className="px-6 lg:px-16 pb-8 sticky-filter" data-testid="work-filters">
          <div className="max-w-[1400px] mx-auto space-y-3">
            <div className="flex flex-wrap gap-2.5">
              {[
                { id: 'all', label: 'All Work' },
                ...MARKET_SECTIONS.map((s) => ({ id: s.id, label: s.id === 'uae' ? 'Dubai, UAE' : s.name })),
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`filter-${f.id}`}
                  onClick={() => setMarketFilter(f.id)}
                  className={`px-4 py-2 border font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    marketFilter === f.id
                      ? 'border-tangerine/70 text-tangerine bg-tangerine/[0.06]'
                      : 'border-white/10 text-slate-400 hover:text-white hover:border-white/25'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2.5">
              {CAPABILITY_FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`capability-filter-${f.id}`}
                  onClick={() => setCapId((prev) => (prev === f.id ? null : f.id))}
                  className={`px-3.5 py-1.5 border font-mono text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    capId === f.id
                      ? 'border-magenta/70 text-magenta bg-magenta/[0.07]'
                      : 'border-white/[0.08] text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <div ref={trackRef} className="px-6 lg:px-16 pb-24">
          <div className="max-w-[1200px] mx-auto relative">
            <Spine trackRef={trackRef} />
            {MARKET_SECTIONS.map((sec) => {
              const ps = filtered.filter((p) => p.cluster === sec.id);
              if (!ps.length) return null;
              let prevGroup = null;
              return (
                <div key={sec.id} className="first:pt-2">
                  <Reveal>
                    <div className="relative pl-12 md:pl-0 pt-14 pb-12">
                      <span className="hidden md:block absolute top-[4.4rem] left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-tangerine" aria-hidden="true" />
                      <p className="font-mono text-[11px] tracking-[0.35em] text-tangerine">
                        MARKET {sec.num}
                      </p>
                      <h2 className="mt-3 font-syne font-extrabold text-3xl sm:text-5xl text-white tracking-tight uppercase" data-testid={`work-market-heading-${sec.id}`}>
                        {sec.id === 'uae' ? 'Dubai, UAE' : sec.name}
                      </h2>
                      <p className="mt-4 font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-violettext" data-testid={`work-market-loc-${sec.id}`}>
                        {sec.heading}
                      </p>
                    </div>
                  </Reveal>
                  {ps.map((p, i) => {
                    const showGroup = p.group && p.group !== prevGroup;
                    prevGroup = p.group;
                    const groupLabel =
                      showGroup && sec.id === 'ind'
                        ? { hul: 'Hindustan Unilever · Oct 2024 — Apr 2025', mediacom: 'MediaCom / WPP Media · May 2018 — Dec 2021', culturemachine: 'Culture Machine', independent: 'Independent Creative Consulting', freelance: 'Freelance Projects' }[p.group]
                        : null;
                    return (
                      <div key={p.id}>
                        {groupLabel && (
                          <div className="relative pl-12 md:pl-0 pb-2">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ultraviolet">
                              {groupLabel}
                            </p>
                          </div>
                        )}
                        <Entry p={p} num={PROJECTS.indexOf(p) + 1} side={i % 2 === 0 ? 'left' : 'right'} />
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="relative pl-12 md:pl-0 py-24 text-center" data-testid="work-empty-state">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                  No projects match this filter
                </p>
                <button
                  onClick={() => {
                    setMarketFilter('all');
                    setCapId(null);
                  }}
                  className="mt-6 px-5 py-3 border border-white/15 hover:border-tangerine/70 transition-colors font-mono text-[10px] uppercase tracking-[0.25em] text-white"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </main>
    </div>
  );
}
